import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { SpaceNames } from '@/components/SpaceNames';
import { useRowClick } from '@/lib/navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { Model } from '@rock/types';
import { orderBy } from 'lodash';
import type { FC } from 'react';
import { useMemo } from 'react';

interface BrandModelsTabProps {
    /** Live models only; an archived model (a merged one included) is only counted. */
    models: Model[];
    archivedCount: number;
}

/** Every live catalog model linked to this brand (`Model.brandId`), linking through to the model pages. */
export const BrandModelsTab: FC<BrandModelsTabProps> = ({ models, archivedCount }) => {
    const onRowClick = useRowClick();
    const sorted = useMemo(() => orderBy(models, (model) => model.modelCanonical), [models]);

    return (
        <Card>
            <CardHeader>
                <CardTitle>Models ({models.length})</CardTitle>
                <CardDescription>
                    Catalog models whose brand is linked here.
                    {archivedCount > 0 &&
                        ` ${archivedCount} archived or merged ${archivedCount === 1 ? 'model is' : 'models are'} left out.`}
                </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
                {sorted.length === 0 ? (
                    <div className="section-description">
                        {archivedCount > 0 ? 'No live models link to this brand.' : 'No models link to this brand yet.'}{' '}
                        Models pick up a brand from enrichment, the manuals worker's suggestions, or a manual link on
                        the model page.
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="min-w-[180px]">Model</TableHead>
                                <TableHead className="min-w-[140px]">Space</TableHead>
                                <TableHead className="min-w-[160px]">Type</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sorted.map((model) => (
                                <TableRow
                                    key={model.id}
                                    className="cursor-pointer transition-colors hover:bg-muted/50"
                                    onClick={onRowClick(`/products/models/${encodeURIComponent(model.id)}/details`)}
                                >
                                    <TableCell className="font-medium">{model.modelDisplay}</TableCell>
                                    <TableCell>
                                        <SpaceNames spaceIds={[model.spaceId]} />
                                    </TableCell>
                                    <TableCell>
                                        <EquipmentTypeBadge typeId={model.typeId} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};
